import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import QRCode from "qrcode";
import api from "../api";
import formatDate from "./formatDate";

export const generatePDF = async ({
  titleData,
  sections,
  qrData,
  textTranslator,
  pageKey,
  defaultLanguage,
  dispatch,
  showAlert,
}) => {
  try {
    // ── CLINIC DATA ─────────────────────────────
    const clinicRes = await api.get(`/users/clinic-info`);
    const clinic = clinicRes.data?.data;

    const t = (key, sectionKey) => textTranslator(key, sectionKey) || key;

    const isRTL = defaultLanguage === "ar";
    const lang = isRTL ? "ar" : "en";

    // ── STATIC TEXT ─────────────────────────────
    const staticText = {
      en: {
        "Generated on": "Generated on",
        "Failed to generate PDF": "Failed to generate PDF",
        fileType: "PDF Document",
      },
      fr: {
        "Generated on": "Généré le",
        "Failed to generate PDF": "Échec de la génération du PDF",
        fileType: "PDF Document",
      },
      ar: {
        "Generated on": "تم الإنشاء في",
        "Failed to generate PDF": "فشل في إنشاء ملف PDF",
        fileType: "مستند PDF",
      },
    };

    // ── LOGO ─────────────────────────────
    const clinicLogo = clinic?.logo
      ? clinic.logo
      : null;

    const clinicInitials =
      clinic?.name
        ?.split(" ")
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase() || "CL";

    // ── QR GENERATION ─────────────────────────────
    let qrImage = null;

    if (qrData) {
      try {
        qrImage = await QRCode.toDataURL(JSON.stringify(qrData), {
          width: 120,
          margin: 1,
        });
      } catch (err) {
        console.warn("QR generation failed:", err);
      }
    }

    // ── ROW HELPER ─────────────────────────────
    const row = (label, value, page) => `
      <div class="row">
        <div class="row-label">${label}</div>
        <div class="row-value">${value || "N/A"}</div>
      </div>`;

    // ── CONTAINER ─────────────────────────────
    const container = document.createElement("div");
    container.style.cssText = `
      position: fixed;
      top: -99999px;
      left: -99999px;
      width: 794px;
      background: #fff;
      font-family: 'Segoe UI', Tahoma, 'Noto Sans Arabic', Arial, sans-serif;
      direction: ${isRTL ? "rtl" : "ltr"};
    `;

    // ── DYNAMIC SECTIONS ─────────────────────────────

    const getSectionKey = (section) => {
      if (section.pageKey) {
        return section.pageKey;
      }
      return pageKey;
    };

    const sectionsHTML = sections
    
      .map(
        (section) => `
      <div class="card">
        <div class="card-title">${t(section.title, getSectionKey(section))}</div>
        <div class="card-body">
          ${section.items
            .map((item) => row(t(item.label, getSectionKey(section)), item.value))
            .join("")}
           
        </div>
      </div>
    `
      )
      .join("");

    container.innerHTML = `
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }

        .pdf-wrap {
          padding: 36px;
          background: #fff;
          text-align: ${isRTL ? "right" : "left"};
        }

        /* HEADER */
        .header {
          background: linear-gradient(135deg, #00558d 0%, #0077c2 100%);
          border-radius: 14px;
          padding: 22px 26px;
          margin-bottom: 22px;
          color: #fff;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .clinic-logo,
        .clinic-fallback-logo {
          width: 52px;
          height: 52px;
          border-radius: 10px;
        }

        .clinic-logo {
          object-fit: cover;
          background: white;
        }

        .clinic-fallback-logo {
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          background: rgba(255,255,255,0.18);
          color: white;
        }

        .header h1 {
          font-size: 20px;
          margin-bottom: 6px;
        }

        .header p {
          font-size: 12px;
          opacity: 0.85;
          line-height: 1.5;
        }

        /* QR */
        .qr {
          width: 70px;
          height: 70px;
          border-radius: 8px;
          background: white;
        }

        /* CARD */
        .card {
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          margin-bottom: 16px;
          overflow: hidden;
        }

        .card-title {
          background: #f8fafc;
          padding: 10px 20px;
          font-size: 11px;
          font-weight: 700;
          color: #64748b;
          text-transform: uppercase;
        }

        .card-body {
          padding: 10px 20px;
        }

        /* ROW */
        .row {
          display: flex;
          padding: 10px 0;
          border-bottom: 1px solid #f1f5f9;
        }

        .row:last-child {
          border-bottom: none;
        }

        .row-label {
          width: 180px;
          font-size: 12px;
          color: #64748b;
        }

        .row-value {
          font-size: 13px;
          font-weight: 600;
          color: #0f172a;
          flex: 1;
        }

        /* FOOTER */
        .footer {
          margin-top: 24px;
          padding-top: 12px;
          border-top: 1px solid #e2e8f0;
          display: flex;
          justify-content: space-between;
          font-size: 10px;
          color: #94a3b8;
        }
      </style>

      <div class="pdf-wrap">

        <!-- HEADER -->
        <div class="header">

          <div class="header-left">
            ${
              clinicLogo
                ? `<img class="clinic-logo" src="${clinicLogo}" />`
                : `<div class="clinic-fallback-logo">${clinicInitials}</div>`
            }

            <div>
              <h1>${clinic?.name || "Clinic"}</h1>
              <p>
                ${clinic?.address || ""}<br/>
                ${clinic?.phone || ""} · ${clinic?.email || ""}
              </p>
              <p style="margin-top:6px;">
                ${titleData?.subtitle || ""}
              </p>
            </div>
          </div>

          ${
            qrImage
              ? `<img class="qr" src="${qrImage}" />`
              : ""
          }

        </div>

        <!-- SECTIONS -->
        ${sectionsHTML}

        <!-- FOOTER -->
        <div class="footer">
          <span>
            ${staticText[lang]["Generated on"]} ${formatDate(new Date(), true)}
          </span>
          <span>${clinic?.name || "System"}</span>
          <span>${staticText[lang]["fileType"]}</span>
        </div>

      </div>
    `;

    document.body.appendChild(container);

    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
    });

    document.body.removeChild(container);

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();

    const imgData = canvas.toDataURL("image/png");
    const imgH = pageW * (canvas.height / canvas.width);

    if (imgH <= pageH) {
      doc.addImage(imgData, "PNG", 0, 0, pageW, imgH);
    } else {
      const pxPerMm = canvas.width / pageW;
      const slicePx = pageH * pxPerMm;

      let yPx = 0;
      let pageNum = 0;

      while (yPx < canvas.height) {
        if (pageNum > 0) doc.addPage();
        const yMm = -(yPx / pxPerMm);
        doc.addImage(imgData, "PNG", 0, yMm, pageW, imgH);
        yPx += slicePx;
        pageNum++;
      }
    }

    doc.save(`${titleData?.filename || "document"}_${Date.now()}.pdf`);
  } catch (error) {
    console.error("PDF generation failed:", error);

    dispatch(
      showAlert({
        type: "error",
        title: textTranslator("Error", pageKey) || "Error",
        message:
          textTranslator("Failed to generate PDF", pageKey) ||
          "Failed to generate PDF",
      })
    );
  }
};